import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { deletePaginationParams } from '@occc-app/hooks/usePaginationParams';

import { ConnectionStatus } from '../retailer-network-my-stores-v2-page.types';
import {
  FILTER_PARAM_NAMES,
  isValidConnectionStatus,
} from '../retailer-network-my-stores-v2-page.utils';

type UseMyStoresFiltersResult = {
  stateFilter: string;
  cityFilter: string;
  connectionStatusFilter: ConnectionStatus | '';
  search: string;
  selectedLocationUuid: string | null;
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onConnectionStatusChange: (value: ConnectionStatus | '') => void;
  onSearchChange: (value: string) => void;
  onReset: () => void;
  onPinClick: (locationUuid: string) => void;
};

// Search fires on every keystroke (see StoreFilterBar) and now drives a server request —
// debounce the URL/query update so typing doesn't trigger a round-trip per character.
const SEARCH_DEBOUNCE_MS = 300;

export const useStoresFilters = (): UseMyStoresFiltersResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedLocationUuid, setSelectedLocationUuid] = useState<string | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The debounced search update below fires after a delay, by which point a newer render (and
  // a newer `location.search`) may already exist — read from this ref instead of closing over
  // `location.search` so a State/City/Status change made while typing isn't clobbered.
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  const searchParams = new URLSearchParams(location.search);
  const stateFilter = searchParams.get(FILTER_PARAM_NAMES.state) ?? '';
  // City is only meaningful alongside a State — the UI disables the City dropdown until one is
  // chosen, so ignore any `city` param left in (or hand-crafted into) the URL without it.
  const cityFilter = stateFilter ? searchParams.get(FILTER_PARAM_NAMES.city) ?? '' : '';
  const connectionStatusFilter = isValidConnectionStatus(
    searchParams.get(FILTER_PARAM_NAMES.connectionStatus),
  )
    ? (searchParams.get(FILTER_PARAM_NAMES.connectionStatus) as ConnectionStatus)
    : '';
  const search = (searchParams.get(FILTER_PARAM_NAMES.search) ?? '').trim();

  const updateFilterParams = (
    values: Partial<Record<string, string>>,
    currentSearch: string = location.search,
  ) => {
    const next = new URLSearchParams(currentSearch);
    Object.entries(values).forEach(([paramName, value]) => {
      if (value) {
        next.set(paramName, value);
      } else {
        next.delete(paramName);
      }
    });
    // Any filter/search change must not carry over a pagination cursor from the previous
    // result set — otherwise the flat view's next fetch can use a cursor that belongs to a
    // different query entirely.
    deletePaginationParams(next);
    navigate({ search: next.toString() }, { replace: true, state: location.state });
  };

  const updateFilterParam = (paramName: string, value: string) =>
    updateFilterParams({ [paramName]: value });

  const resetPin = () => setSelectedLocationUuid(null);

  return {
    stateFilter,
    cityFilter,
    connectionStatusFilter,
    search,
    selectedLocationUuid,
    onStateChange: (value) => {
      // City is dependent on State — clear it in the same navigation so the two never
      // disagree (a City without its parent State is meaningless).
      updateFilterParams({ [FILTER_PARAM_NAMES.state]: value, [FILTER_PARAM_NAMES.city]: '' });
      resetPin();
    },
    onCityChange: (value) => {
      updateFilterParam(FILTER_PARAM_NAMES.city, value);
      resetPin();
    },
    onConnectionStatusChange: (value) => {
      updateFilterParam(FILTER_PARAM_NAMES.connectionStatus, value);
      resetPin();
    },
    onSearchChange: (value) => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        updateFilterParams(
          { [FILTER_PARAM_NAMES.search]: value.trim() },
          locationSearchRef.current,
        );
      }, SEARCH_DEBOUNCE_MS);
      resetPin();
    },
    onReset: () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      updateFilterParams({
        [FILTER_PARAM_NAMES.state]: '',
        [FILTER_PARAM_NAMES.city]: '',
        [FILTER_PARAM_NAMES.connectionStatus]: '',
        [FILTER_PARAM_NAMES.search]: '',
      });
      resetPin();
    },
    onPinClick: (locationUuid) =>
      setSelectedLocationUuid((prev) => (prev === locationUuid ? null : locationUuid)),
  };
};
